<script setup lang="ts">
    import { computed, provide, reactive } from 'vue';
    import { PanelTabsKey, type PanelTabRegistration } from './panelTabs';

    // Optional external control; when unbound the component manages its own
    // active tab and defaults to the first declared <PanelTab>.
    const model = defineModel<string | number | undefined>();

    // Children register themselves (in mount/DOM order) so the tab strip can be
    // derived automatically from the declared <PanelTab>s — no items array.
    const registrations = reactive<PanelTabRegistration[]>([]);

    const isSameValue = (a: string | number | undefined, b: string | number | undefined) =>
        String(a) === String(b);

    // A model value with no registered tab (unbound, or the tab was removed by
    // v-if) falls back to the tab flagged `default`, else the first.
    const activeValue = computed(
        () =>
            registrations.find((t) => isSameValue(t.value, model.value))?.value ??
            registrations.find((t) => t.isDefault)?.value ??
            registrations[0]?.value
    );

    const isActive = (value: string | number) => isSameValue(value, activeValue.value);

    provide(PanelTabsKey, {
        register: (tab) => registrations.push(tab),
        unregister: (id) => {
            const i = registrations.findIndex((t) => t.id === id);
            if (i !== -1) registrations.splice(i, 1);
        },
        isActive,
    });
</script>

<template>
    <div>
        <!-- Tab strip merges with the panel: each tab's -mb-px overlaps the
             panel's top border, and the active tab's white background hides the
             border beneath it so the two read as one surface. -->
        <div class="flex gap-1 items-end relative z-10" role="tablist">
            <button
                v-for="tab in registrations"
                :id="`${tab.id}-tab`"
                :key="tab.value"
                type="button"
                role="tab"
                :aria-selected="isActive(tab.value)"
                :aria-controls="`${tab.id}-panel`"
                class="px-5 pt-2 text-base rounded-t-sm border-t border-x cursor-pointer custom-focus"
                :class="
                    isActive(tab.value)
                        ? 'bg-white -mb-px pb-2 border-gray-light text-primary-avocado'
                        : 'bg-gray-lightest pb-1 border-gray-lightest text-gray-dark hover:text-primary-avocado'
                "
                @click="model = tab.value"
            >
                {{ tab.label }}
            </button>
        </div>
        <div class="border border-gray-light rounded-lg rounded-tl-none bg-white p-6 relative z-9">
            <!-- All <PanelTab>s live here; each shows itself only when active. -->
            <slot />
        </div>
    </div>
</template>
